// =============================
// Imports
// =============================

import { Component } from 'react';
import PropTypes from 'prop-types';
import _some from 'lodash/some';
import { compose } from 'redux';

import { withTranslation } from '../../../config/i18n';
import withResponsive from '../../hoc/withResponsive';

import ArtistItem from '../../presentationals/artistitem';
import Cover from '../../containers/cover';
import { Desktop } from '../../other/responsive';
import Link from '../../other/link';
import PageWrapper from '../../other/pagewrapper';
import Pagination from '../../presentationals/pagination';

import * as entity from '../../../helpers/entity';
import * as pth from '../../../helpers/proptypes';
import { transformTotalValueToString } from '../../../helpers/search';

import {
  Name,
  EntityItemList,
  ListContent,
  ListFooter,
  NoResults,
  Title,
} from '../entity.styles';

// =============================
// Component
// =============================

class EntitySearch extends Component {
  static propTypes = {
    i18n: PropTypes.shape({
      language: PropTypes.string,
    }).isRequired,
    /** If True, user is on a mobile device. */
    isMobile: PropTypes.bool.isRequired,
    results: PropTypes.arrayOf(pth.entitysearchdoc).isRequired,
    t: PropTypes.func.isRequired,
    total: PropTypes.shape({
      value: PropTypes.number,
      relation: PropTypes.oneOf(['gte', 'eq']),
    }).isRequired,
    query: PropTypes.string,
    page: PropTypes.number.isRequired,
    nbPages: PropTypes.number.isRequired,
    type: PropTypes.oneOf(['albums', 'artists', 'catalogs', 'labels', 'playlists', 'publishers'])
      .isRequired,
  };

  static defaultProps = {
    query: '',
  };

  getResultNumber = () => {
    const { query, total, type, t } = this.props;

    if (!query) {
      return `${transformTotalValueToString(total)} ${t(`pages:search.${type}`)}`;
    }

    return t(`pages:search.results_${type}`, { count: total.value, query });
  }

  render() {
    const {
      i18n: { language },
      isMobile,
      results,
      page,
      nbPages,
      t,
      total,
      type,
      query,
    } = this.props;

    // NOTE: Remove "s" to get cover type
    const coverType = type.substr(0, type.length - 1);

    return (
      <PageWrapper type="entity">
        <Desktop>
          <Name asPageTitle>{t('pages:search.search_results')}</Name>
        </Desktop>
        <EntityItemList>
          <Title>
            {this.getResultNumber()}
          </Title>
          <ListContent goVerticalOnMobile col={type === 'artists' ? 1 : 2}>
            {total.value === 0 && <NoResults>{t('pages:search.no_results')}</NoResults>}

            {results.map((r) => {
              const baseRoute = _some(['labels', 'publishers'], a => a === type)
                ? entity.getUrl(type)(r)
                : entity.getBaseRoute(type);

              if (isMobile && type === 'artists') {
                return <ArtistItem artist={r} />;
              }

              return (
                <Cover
                  key={`${type}-${r.id}`}
                  title={entity.getTitle(type)(r, { variation: 'withAlbumRef', t })}
                  src={entity.getMultiCoverUrls(type, 'large')(r)}
                  type={coverType}
                  description={entity.getDescription(type)(r, language)}
                  placeholderType={coverType}
                  LinkComponent={entity.getUrl(type)(r) ? Link : null}
                  linkProps={{
                    route: baseRoute,
                    nextAs: entity.getUrl(type)(r),
                  }}
                  contextName={entity.getPlayerContextName(type)}
                  contextId={r.id}
                />
              );
            })}

          </ListContent>
          <ListFooter>
            <Pagination
              currentPage={page}
              pageCount={nbPages}
              buildSearchLink={nextPage => entity.getEntitySearchUrl(type)(query, nextPage)}
            />
          </ListFooter>
        </EntityItemList>
      </PageWrapper>
    );
  }
}

export default compose(
  withTranslation('pages'),
  withResponsive,
)(EntitySearch);
